import { useMemo, useState } from 'react';
import { BusinessCategoryPicker } from '@/components/common/business-category-picker';
import { Button } from '@/components/ui/button';
import {
  categoryLabel,
  type BusinessCategoryOption,
} from '@/lib/conclave-networking';

type Props = {
  values: BusinessCategoryOption[];
  onChange: (values: BusinessCategoryOption[]) => void;
  placeholder?: string;
};

function optionKey(item: BusinessCategoryOption) {
  return categoryLabel(item);
}

export function CategoryMultiPicker({
  values,
  onChange,
  placeholder = 'Search and add categories…',
}: Props) {
  const [pendingKey, setPendingKey] = useState(0);
  const selectedKeys = useMemo(() => new Set(values.map(optionKey)), [values]);

  return (
    <div className="space-y-3">
      {values.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {values.map((item) => (
            <Button
              key={optionKey(item)}
              type="button"
              variant="secondary"
              size="sm"
              className="h-auto max-w-full rounded-xl px-3 py-2 text-left"
              onClick={() => onChange(values.filter((v) => optionKey(v) !== optionKey(item)))}
              title="Tap to remove"
            >
              <span className="min-w-0">
                <span className="block break-words text-xs font-semibold">{item.subCategory}</span>
                <span className="mt-0.5 block text-[10px] font-medium text-muted-foreground">
                  {item.category}
                </span>
              </span>
              <span className="ml-2 shrink-0 text-muted-foreground" aria-hidden>
                ×
              </span>
            </Button>
          ))}
        </div>
      ) : (
        <p className="rounded-xl border border-dashed border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          No categories selected yet. Search and add below.
        </p>
      )}
      <BusinessCategoryPicker
        key={pendingKey}
        placeholder={placeholder}
        onSelect={(value) => {
          if (!value) return;
          const next = {
            category: value.businessCategory,
            subCategory: value.subCategory,
          };
          if (selectedKeys.has(optionKey(next))) {
            setPendingKey((k) => k + 1);
            return;
          }
          onChange([...values, next]);
          setPendingKey((k) => k + 1);
        }}
      />
    </div>
  );
}
